import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useTabNav } from "../hooks/useTabNav";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Users, FileText, TrendingUp, DollarSign, Shield, MessageSquare, Upload, BarChart3, ChevronDown, ChevronUp, Activity, PieChart, MapPin, UserCheck, UserX, Building2, Clock, Mail, Play, Pause, Target, Zap, ArrowRight, Bell, ChevronRight, AlertCircle, CheckCircle2, Eye } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LuxuryBackground, Card, GoldButton, PageHero } from "../components/shared/UIComponents";
import { toast } from "sonner";
import DealDetailModal from "../components/DealDetailModal";
import Deal360Page from "./Deal360Page";
import CRMLenders from "../components/admin/CRMLenders";
import { fmtUSD } from "../utils/currency";
import InvestorsAdminPanel from "../components/admin/InvestorsAdminPanel";
import AgentPerformancePanel from "../components/admin/AgentPerformancePanel";
import EngagementScoringPanel from "../components/admin/EngagementScoringPanel";
import TasksPanel from "../components/admin/TasksPanel";
import DepartmentChannels from "../components/admin/DepartmentChannels";
import CRMDashboard from "../components/admin/CRMDashboard";
import CRMBorrowers from "../components/admin/CRMBorrowers";
import CRMInvestors from "../components/admin/CRMInvestors";
import EmailOutreachPanel from "../components/admin/EmailOutreachPanel";
import CapitalEngineDashboard from "../components/admin/CapitalEngineDashboard";
import FundDashboard from "../components/admin/FundDashboard";
import SecurityDashboard from "../components/admin/SecurityDashboard";
import DealCommandCenter from "../components/admin/DealCommandCenter";
import DocumentGovernancePanel from "../components/admin/DocumentGovernancePanel";
import MakerCheckerPanel from "../components/admin/MakerCheckerPanel";
import TaxExportPanel from "../components/shared/TaxExportPanel";
import DigestPanel from "../components/admin/DigestPanel";
import ExceptionQueuePanel from "../components/admin/ExceptionQueuePanel";
import IntegrityDashboard from "../components/admin/IntegrityDashboard";
import MigrationDashboard from "../components/admin/MigrationDashboard";
import ConversionDashboard from "../components/admin/ConversionDashboard";
import PlatformHealthPage from "./PlatformHealthPage";
import AdminNavSidebar from "../components/admin/AdminNavSidebar";
import ReportCenterPage from "./ReportCenterPage";
import MarketplacePreview from "../components/admin/MarketplacePreview";
import AdminFunnelAnalytics from "../components/AdminFunnelAnalytics";
import AdminServicingTab from "../components/admin/AdminServicingTab";
import AdminAcquisitionsTab from "../components/admin/AdminAcquisitionsTab";
import CopilotPanel from "../components/CopilotPanel";
import UnifiedInbox from "../components/shared/UnifiedInbox";
import usePullToRefresh from "../hooks/usePullToRefresh";
import { usePageNavigate } from "../hooks/usePageNavigate";
import TeamManagement from "../components/admin/TeamManagement";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, AreaChart, Area, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const loanTypeLabels = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial" };
const stageLabels = { submitted: "Submitted", in_review: "In Review", needs_info: "Needs Info", underwriting: "Underwriting", approved: "Approved", capital_matching: "Capital Matching", terms: "Terms", funding: "Funding", funded: "Funded", active: "Active", declined: "Declined", new_lead: "New Lead", intake_complete: "Intake Complete", intake: "Intake", docs_requested: "Docs Requested", packaging: "Packaging", matched: "Matched", submitted_to_lender: "Submitted to Lender", terms_received: "Terms Received", conditional_approval: "Conditional Approval", clear_to_close: "Clear to Close" };
const stageColors = { submitted: "bg-blue-50 text-blue-600", in_review: "bg-amber-50 text-amber-600", needs_info: "bg-orange-50 text-orange-600", underwriting: "bg-purple-50 text-purple-600", approved: "bg-emerald-50 text-emerald-600", capital_matching: "bg-indigo-50 text-indigo-600", terms: "bg-violet-50 text-violet-600", funding: "bg-teal-50 text-teal-600", funded: "bg-emerald-100 text-emerald-600", active: "bg-green-100 text-green-700", declined: "bg-red-100 text-red-600", new_lead: "bg-cream text-[#0A0A0A]", intake_complete: "bg-blue-50 text-blue-600", intake: "bg-blue-50 text-blue-600", docs_requested: "bg-amber-50 text-amber-600", packaging: "bg-indigo-50 text-indigo-600", matched: "bg-emerald-100 text-emerald-600", submitted_to_lender: "bg-blue-100 text-blue-600", terms_received: "bg-violet-100 text-violet-600", conditional_approval: "bg-teal-100 text-teal-600", clear_to_close: "bg-emerald-200 text-emerald-700" };


export default function AdminDashboard() {
  const { user } = useAuth();
  const { navigateToEntity } = usePageNavigate();
  const location = useLocation();
  const { data, isLoading: loading, refetch: refetchDashboard } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: () => axios.get(`${API}/admin/dashboard`).then(r => r.data),
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });
  const [activeSection, setActiveSection] = useTabNav("section", "overview");
  const [updatingStage, setUpdatingStage] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [pendingLenders, setPendingLenders] = useState([]);
  const [approvingUser, setApprovingUser] = useState(null);

  const [revenueData, setRevenueData] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [fundingDeal, setFundingDeal] = useState(null);
  const [fundForm, setFundForm] = useState({ loan_amount: "", origination_fee_pct: "2.0", platform_fee_pct: "1.0", lender_name: "" });
  const [fundLoading, setFundLoading] = useState(false);
  const [detailLeadId, setDetailLeadId] = useState(null);
  const [deal360OverlayId, setDeal360OverlayId] = useState(null);

  // For CRM sections: open deal 360 as overlay instead of navigating away
  const handleDeal360Navigate = useCallback((type, id) => {
    if (type === "deal") {
      setDeal360OverlayId(id);
    } else {
      navigateToEntity(type, id);
    }
  }, [navigateToEntity]);
  const [allUsers, setAllUsers] = useState([]);
  const [allUsersLoading, setAllUsersLoading] = useState(false);
  const [accreditations, setAccreditations] = useState({ pending: [], pending_count: 0, verified_count: 0 });
  const [reviewingUser, setReviewingUser] = useState(null);

  const { pullIndicator } = usePullToRefresh(refetchDashboard);

  const fetchData = () => refetchDashboard();

  const fetchAllUsers = () => {
    setAllUsersLoading(true);
    axios.get(`${API}/admin/users`).then(r => setAllUsers(r.data.users || [])).catch(console.error).finally(() => setAllUsersLoading(false));
  };

  useEffect(() => {
    if (activeSection === "analytics" && !analyticsData) {
      setAnalyticsLoading(true);
      axios.get(`${API}/analytics/overview`).then(r => setAnalyticsData(r.data)).catch(console.error).finally(() => setAnalyticsLoading(false));
    }
    if (activeSection === "approvals") {
      axios.get(`${API}/admin/pending-lenders`).then(r => setPendingLenders(r.data.pending_lenders || [])).catch(console.error);
      axios.get(`${API}/investor/admin/accreditation/pending`).then(r => setAccreditations(r.data)).catch(console.error);
    }
    if (activeSection === "users" && allUsers.length === 0) {
      fetchAllUsers();
    }
    if (activeSection === "revenue" && !revenueData) {
      setRevenueLoading(true);
      axios.get(`${API}/revenue/summary`).then(r => setRevenueData(r.data)).catch(console.error).finally(() => setRevenueLoading(false));
    }
  }, [activeSection, analyticsData, revenueData]);

  const handleApproval = async (userId, action) => {
    setApprovingUser(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/${action}`);
      setPendingLenders(prev => prev.filter(l => l.user_id !== userId));
      fetchData();
    } catch (err) { console.error(err); }
    finally { setApprovingUser(null); }
  };

  const handleFundDeal = async (leadId) => {
    if (!fundForm.loan_amount) return;
    setFundLoading(true);
    try {
      await axios.post(`${API}/deals/${leadId}/funded`, {
        loan_amount: parseFloat(fundForm.loan_amount),
        origination_fee_pct: parseFloat(fundForm.origination_fee_pct),
        platform_fee_pct: parseFloat(fundForm.platform_fee_pct),
        lender_name: fundForm.lender_name || null,
      });
      setFundingDeal(null);
      setFundForm({ loan_amount: "", origination_fee_pct: "2.0", platform_fee_pct: "1.0", lender_name: "" });
      setRevenueData(null);
      fetchData();
    } catch (err) { console.error(err); }
    finally { setFundLoading(false); }
  };

  const updateStage = async (leadId, stage) => {
    setUpdatingStage(leadId);
    try {
      await axios.post(`${API}/admin/leads/${leadId}/stage?stage=${stage}`);
      toast.success(`Deal moved to ${stageLabels[stage] || stage}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Stage update failed");
    }
    finally { setUpdatingStage(null); }
  };

  if (loading) return (
    <div className="relative min-h-[60vh] flex items-center justify-center"><LuxuryBackground />
      <Loader2 className="relative z-10 h-8 w-8 animate-spin text-emerald-700" /></div>
  );

  const s = data?.stats || {};

  return (
    <div className="relative" data-testid="admin-dashboard">
      {pullIndicator}
      <PageHero compact eyebrow="Admin" title="Platform Command Center"
        description="Monitor deals, manage users, and oversee the lending marketplace."
        rightContent={null} />
      <div className="relative">
        <LuxuryBackground />
        <section className="relative z-10 mx-auto max-w-[1440px] px-4 py-8 lg:px-6">
          <div className="md:flex gap-6">
            {/* Smart Navigation Sidebar */}
            <AdminNavSidebar
              activeSection={activeSection}
              onSectionChange={setActiveSection}
              onEntityNavigate={navigateToEntity}              pendingCount={(s.pending_lenders || 0) + (accreditations.pending_count || 0) + (s.pending_lender_approvals || 0)}
            />

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Stats Strip (compact) */}
              <div className="grid gap-3 grid-cols-2 sm:grid-cols-4 mb-6">
                {[
                  { label: "Users", value: s.total_users || 0, sub: `${s.borrowers || 0}B / ${s.lenders_investors || 0}L`, icon: Users, color: "text-blue-600", bg: "bg-blue-50 border-blue-200" },
                  { label: "Funded Volume", value: fmtUSD(s.total_volume || 0), sub: `Pipeline: ${fmtUSD(s.pipeline_volume || 0)}`, icon: TrendingUp, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
                  { label: "Term Sheets", value: s.total_term_sheets || 0, sub: `${s.total_interests || 0} interests`, icon: FileText, color: "text-[#E5C98B]", bg: "bg-amber-50 border-amber-200" },
                  { label: "Documents", value: s.total_documents || 0, sub: `${s.total_inquiries || 0} inquiries`, icon: Upload, color: "text-[#444]", bg: "bg-[#FDFCF9] border-[#EDE8DC]" },
                ].map((item, i) => (
                  <motion.div key={item.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                    <div className="rounded-2xl bg-white border border-[#EDE8DC] p-3 hover:shadow-[0_4px_20px_rgba(6,95,70,0.08)] hover:border-emerald-200 transition-all">
                      <div className="flex items-center gap-2">
                        <div className={`rounded-xl ${item.bg} p-1.5`}><item.icon className={`h-4 w-4 ${item.color}`} /></div>
                        <div>
                          <div className="text-lg font-bold text-[#0A0A0A] leading-tight tabular-nums">{item.value}</div>
                          <div className="text-[10px] text-[#888]">{item.label} <span className="text-[#bbb]">{item.sub}</span></div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Report Center navigation */}
              {activeSection === "report_center" && (
                <ReportCenterPage onViewDeal={(leadId) => { setDetailLeadId(leadId); setActiveSection("command_center"); }} />
              )}

          {/* Deal Command Center */}
          {activeSection === "command_center" && (
            <DealCommandCenter onViewDeal={(leadId) => setDetailLeadId(leadId)} onEntityNavigate={navigateToEntity} />
          )}

          {/* Pipeline Stages (overview) */}
          {activeSection === "overview" && (
            <Card hover={false} className="mb-6 !p-3">
              <h4 className="text-xs font-bold text-[#999] uppercase tracking-wider mb-2">Pipeline Stages</h4>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(s.stages || {}).map(([stage, count]) => (
                  <div key={stage} className="rounded-lg border border-cream-warm bg-cream px-3 py-1.5 text-center min-w-[80px]">
                    <div className="text-base font-bold text-[#0A0A0A]">{count}</div>
                    <div className="text-[9px] text-[#888] capitalize">{stage.replace(/_/g, " ")}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Needs Attention — quick glance for overview */}
          {activeSection === "overview" && (data?.recent_leads || []).length > 0 && (
            <Card hover={false} className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-[#0A0A0A]">Needs Attention</h3>
                <button onClick={() => setActiveSection("command_center")} className="text-[10px] font-semibold text-emerald-600 hover:text-emerald-800 transition-colors" data-testid="go-to-deal-center">
                  View All in Deal Center &rarr;
                </button>
              </div>
              <div className="space-y-2">
                {(data?.recent_leads || []).filter(l => {
                  const comp = l.completeness || {};
                  return !comp.ready_for_review || l.stage === "needs_info" || l.stage === "submitted";
                }).slice(0, 5).map(lead => {
                  const comp = lead.completeness || {};
                  return (
                    <div key={lead.lead_id} className="flex items-center gap-3 p-2.5 rounded-xl border border-[#EDE8DC] hover:border-emerald-200 hover:bg-[#faf8f3] transition-all cursor-pointer"
                      onClick={() => navigateToEntity("deal", lead.lead_id)} data-testid={`attention-deal-${lead.lead_id}`}>
                      <div className={`h-2 w-2 rounded-full shrink-0 ${!comp.ready_for_review ? "bg-amber-400" : lead.stage === "submitted" ? "bg-blue-400" : "bg-orange-400"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-[#0A0A0A] truncate">{lead.full_name || "Unknown Borrower"}</span>
                          {lead.deal_number && <span className="text-[10px] font-mono text-emerald-600">{lead.deal_number}</span>}
                        </div>
                        <div className="text-[10px] text-[#888]">
                          {!comp.ready_for_review && (comp.docs_needed || []).length > 0 ? `Missing ${(comp.docs_needed || []).length} docs` : stageLabels[lead.stage] || lead.stage}
                          {lead.property_address && ` — ${lead.property_address}`}
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold ${stageColors[lead.stage] || "bg-slate-100 text-slate-600"}`}>
                        {stageLabels[lead.stage] || lead.stage}
                      </span>
                      {lead.health_score != null && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ${
                          lead.health_band === "excellent" ? "bg-emerald-100 text-emerald-700" :
                          lead.health_band === "good" ? "bg-emerald-50 text-emerald-600" :
                          lead.health_band === "fair" ? "bg-amber-50 text-amber-600" :
                          lead.health_band === "needs_attention" ? "bg-orange-50 text-orange-600" :
                          "bg-red-50 text-red-600"
                        }`} data-testid={`health-score-${lead.lead_id}`}>
                          {lead.health_score}
                        </span>
                      )}
                      {lead.pending_lender_approval && (
                        <span className="px-2 py-0.5 rounded-lg text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200 whitespace-nowrap" data-testid={`lender-pending-${lead.lead_id}`}>
                          Lender Selection Pending
                        </span>
                      )}
                      <span className="text-xs font-semibold text-[#0A0A0A]">{fmtUSD(lead.requested_loan_amount || lead.purchase_price || 0)}</span>
                    </div>
                  );
                })}
                {(data?.recent_leads || []).filter(l => !l.completeness?.ready_for_review || l.stage === "needs_info" || l.stage === "submitted").length === 0 && (
                  <div className="text-center py-6 text-xs text-[#999]">All deals are on track</div>
                )}
              </div>
            </Card>
          )}

          {/* Users Table */}
          {(activeSection === "overview" || activeSection === "users") && (
            <UserManagementSection
              users={activeSection === "users" ? allUsers : (data?.recent_users || [])}
              loading={activeSection === "users" && allUsersLoading}
              isFullView={activeSection === "users"}
              onRoleChanged={() => { fetchData(); fetchAllUsers(); }}
            />
          )}

          {/* Inquiries */}
          {activeSection === "inquiries" && (
            <Card hover={false}>
              <h3 className="text-lg font-bold text-[#0A0A0A] mb-4">Recent Inquiries</h3>
              <div className="space-y-3">
                {(data?.recent_inquiries || []).map((inq, i) => (
                  <div key={inq.inquiry_id || i} className="rounded-xl border border-cream-warm bg-cream p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-[#0A0A0A]">{inq.full_name || "Unknown Contact"}</span>
                      <span className="px-2 py-1 rounded-lg text-xs bg-gold/15 text-gold-dark">{inq.type}</span>
                    </div>
                    <div className="text-sm text-[#555]">{inq.email} {inq.phone ? `/ ${inq.phone}` : ""}</div>
                    {inq.interest_summary && <div className="mt-2 text-sm text-[#444]">{inq.interest_summary}</div>}
                    <div className="text-xs text-[#aaa] mt-2">{inq.created_at ? new Date(inq.created_at).toLocaleString() : ""}</div>
                  </div>
                ))}
                {(!data?.recent_inquiries || data.recent_inquiries.length === 0) && (
                  <div className="text-center py-8 text-[#999]">No inquiries yet</div>
                )}
              </div>
            </Card>
          )}

          {/* Approvals */}
          {activeSection === "approvals" && (
            <Card hover={false} className="mb-6">
              <h3 className="text-lg font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2"><UserCheck className="h-5 w-5 text-gold-dark" /> Pending Lender Approvals</h3>
              {pendingLenders.length === 0 ? (
                <div className="text-center py-8 text-[#999]">No pending approvals</div>
              ) : (
                <div className="space-y-4">
                  {pendingLenders.map((lender) => (
                    <motion.div key={lender.user_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      className="rounded-xl border border-cream-warm bg-cream p-5" data-testid={`pending-lender-${lender.user_id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-base font-medium text-[#0A0A0A]">{lender.full_name}</span>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-600 border border-amber-400/30">PENDING</span>
                          </div>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                            <div className="flex items-center gap-1.5 text-[#555]"><Building2 className="h-3.5 w-3.5 text-gold/50" />{lender.company || "—"}</div>
                            <div className="flex items-center gap-1.5 text-[#555]"><Shield className="h-3.5 w-3.5 text-gold/50" />{lender.company_type?.replace("_", " ") || "—"}</div>
                            <div className="flex items-center gap-1.5 text-[#555]"><MapPin className="h-3.5 w-3.5 text-gold/50" />{lender.location || "—"}</div>
                            <div className="flex items-center gap-1.5 text-[#555]"><Clock className="h-3.5 w-3.5 text-gold/50" />{lender.created_at ? new Date(lender.created_at).toLocaleDateString() : "—"}</div>
                          </div>
                          {lender.lending_criteria && (
                            <div className="mt-2 flex gap-2 flex-wrap">
                              {(lender.lending_criteria.loan_types || []).map(lt => (
                                <span key={lt} className="px-2 py-0.5 rounded-md text-[10px] bg-gold/10 text-emerald-700 border border-cream-warm">{loanTypeLabels[lt] || lt}</span>
                              ))}
                              {lender.lending_criteria.states && <span className="px-2 py-0.5 rounded-md text-[10px] bg-blue-400/10 text-blue-600/80 border border-blue-400/15">States: {lender.lending_criteria.states}</span>}
                              {lender.aum && <span className="px-2 py-0.5 rounded-md text-[10px] bg-emerald-400/10 text-emerald-600/80 border border-emerald-400/15">AUM: {lender.aum}</span>}
                              {lender.nmls_id && <span className="px-2 py-0.5 rounded-md text-[10px] bg-cream/10 text-[#555] border border-cream/15">NMLS: {lender.nmls_id}</span>}
                            </div>
                          )}
                          <div className="text-xs text-[#999] mt-1">{lender.email} {lender.phone ? `/ ${lender.phone}` : ""}</div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button onClick={() => handleApproval(lender.user_id, "approve")} disabled={approvingUser === lender.user_id}
                            data-testid={`approve-btn-${lender.user_id}`}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500/20 text-emerald-600 border border-emerald-200 text-xs font-medium hover:bg-emerald-500/30 transition-colors disabled:opacity-50">
                            {approvingUser === lender.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />}
                            Approve
                          </button>
                          <button onClick={() => handleApproval(lender.user_id, "reject")} disabled={approvingUser === lender.user_id}
                            data-testid={`reject-btn-${lender.user_id}`}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-500/15 text-red-600 border border-red-400/30 text-xs font-medium hover:bg-red-500/25 transition-colors disabled:opacity-50">
                            <UserX className="h-3.5 w-3.5" />Reject
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Investor Accreditation Review */}
          {activeSection === "approvals" && (
            <Card hover={false} className="mb-6">
              <h3 className="text-lg font-semibold text-[#0A0A0A] mb-1 flex items-center gap-2">
                <Shield className="h-5 w-5 text-gold-dark" /> Investor Accreditation Review
              </h3>
              <div className="flex gap-3 mb-4">
                <span className="text-xs text-[#999]">Pending: <span className="font-bold text-amber-600">{accreditations.pending_count || 0}</span></span>
                <span className="text-xs text-[#999]">Verified: <span className="font-bold text-emerald-600">{accreditations.verified_count || 0}</span></span>
                <span className="text-xs text-[#999]">Rejected: <span className="font-bold text-red-500">{accreditations.rejected_count || 0}</span></span>
              </div>
              {(accreditations.pending || []).length === 0 ? (
                <div className="text-center py-8 text-[#999]">No pending accreditation reviews</div>
              ) : (
                <div className="space-y-4">
                  {(accreditations.pending || []).map((profile) => (
                    <motion.div key={profile.user_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      className="rounded-xl border border-cream-warm bg-cream p-5" data-testid={`accred-review-${profile.user_id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-base font-medium text-[#0A0A0A]">{profile.investor_name || "Investor"}</span>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-600 border border-amber-400/30">PENDING REVIEW</span>
                          </div>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                            <div className="flex items-center gap-1.5 text-[#555]"><Building2 className="h-3.5 w-3.5 text-gold/50" />{profile.entity_type?.replace("_", " ") || "—"}</div>
                            <div className="flex items-center gap-1.5 text-[#555]"><Shield className="h-3.5 w-3.5 text-gold/50" />{profile.accreditation_method?.replace(/_/g, " ") || "—"}</div>
                            <div className="flex items-center gap-1.5 text-[#555]"><MapPin className="h-3.5 w-3.5 text-gold/50" />{profile.city && profile.state ? `${profile.city}, ${profile.state}` : "—"}</div>
                            <div className="flex items-center gap-1.5 text-[#555]"><Clock className="h-3.5 w-3.5 text-gold/50" />{profile.accreditation_submitted_at ? new Date(profile.accreditation_submitted_at).toLocaleDateString() : "—"}</div>
                          </div>
                          {profile.accreditation_method_label && (
                            <div className="mt-2">
                              <span className="px-2 py-0.5 rounded-md text-[10px] bg-blue-400/10 text-blue-600/80 border border-blue-400/15">{profile.accreditation_method_label}</span>
                            </div>
                          )}
                          {profile.accreditation_details && (
                            <div className="mt-2 text-xs text-[#666] italic">"{profile.accreditation_details}"</div>
                          )}
                          <div className="text-xs text-[#999] mt-1">{profile.investor_email} {profile.phone ? `/ ${profile.phone}` : ""}</div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button onClick={async () => {
                            setReviewingUser(profile.user_id);
                            try {
                              await axios.post(`${API}/investor/admin/accreditation/${profile.user_id}/review`, { status: "verified" });
                              axios.get(`${API}/investor/admin/accreditation/pending`).then(r => setAccreditations(r.data));
                            } catch (err) { alert(err.response?.data?.detail || "Failed"); }
                            finally { setReviewingUser(null); }
                          }} disabled={reviewingUser === profile.user_id}
                            data-testid={`accred-approve-${profile.user_id}`}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500/20 text-emerald-600 border border-emerald-200 text-xs font-medium hover:bg-emerald-500/30 transition-colors disabled:opacity-50">
                            {reviewingUser === profile.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />}
                            Verify
                          </button>
                          <button onClick={async () => {
                            setReviewingUser(profile.user_id);
                            const notes = prompt("Rejection reason (optional):");
                            try {
                              await axios.post(`${API}/investor/admin/accreditation/${profile.user_id}/review`, { status: "rejected", review_notes: notes || "" });
                              axios.get(`${API}/investor/admin/accreditation/pending`).then(r => setAccreditations(r.data));
                            } catch (err) { alert(err.response?.data?.detail || "Failed"); }
                            finally { setReviewingUser(null); }
                          }} disabled={reviewingUser === profile.user_id}
                            data-testid={`accred-reject-${profile.user_id}`}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-500/15 text-red-600 border border-red-400/30 text-xs font-medium hover:bg-red-500/25 transition-colors disabled:opacity-50">
                            <UserX className="h-3.5 w-3.5" />Reject
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Tasks Tab */}
          {activeSection === "tasks" && (
            <TasksPanel />
          )}

          {/* Channels Tab */}
          {activeSection === "channels" && (
            <DepartmentChannels />
          )}

          {/* Conversion Analytics Dashboard */}
          {activeSection === "conversion" && (
            <ConversionDashboard onEntityNavigate={navigateToEntity} />
          )}

          {/* Capital Engine Tab */}
          {activeSection === "capital" && (
            <CapitalEngineDashboard onEntityNavigate={navigateToEntity} />
          )}

          {/* Servicing Tab */}
          {activeSection === "servicing" && (
            <AdminServicingTab />
          )}

          {/* Acquisitions Showcase Tab */}
          {activeSection === "acquisitions" && (
            <AdminAcquisitionsTab />
          )}

          {/* Fund I Tab */}
          {activeSection === "funds" && (
            <FundDashboard onEntityNavigate={navigateToEntity} />
          )}

          {/* Opportunities Tab */}
          {activeSection === "opportunities" && (
            <InvestorsAdminPanel />
          )}

          {/* Revenue Tab */}
          {activeSection === "revenue" && (
            <div className="space-y-6" data-testid="admin-revenue">
              {revenueLoading || !revenueData ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
              ) : (
                <>
                  {/* Revenue KPIs */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: "Total Revenue", value: `$${(revenueData.total_revenue / 1e3).toFixed(1)}k`, icon: DollarSign, color: "text-gold-dark" },
                      { label: "Funded Deals", value: revenueData.total_funded, icon: TrendingUp, color: "text-emerald-600" },
                      { label: "Funded Volume", value: `$${(revenueData.total_loan_volume / 1e6).toFixed(2)}M`, icon: Building2, color: "text-blue-600" },
                      { label: "Avg Revenue/Deal", value: fmtUSD(revenueData.avg_revenue_per_deal), icon: Target, color: "text-amber-600" },
                    ].map((kpi, i) => (
                      <motion.div key={kpi.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                        <Card hover={false}>
                          <div className="flex items-center gap-3">
                            <div className="rounded-xl bg-gold/10 p-2.5 border border-cream-warm"><kpi.icon className={`h-5 w-5 ${kpi.color}`} /></div>
                            <div>
                              <div className="text-2xl font-bold text-[#0A0A0A]">{kpi.value}</div>
                              <div className="text-xs text-[#888]">{kpi.label}</div>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </div>

                  {/* Fee Breakdown */}
                  <Card hover={false}>
                    <h3 className="text-lg font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><DollarSign className="h-5 w-5 text-gold-dark" /> Fee Breakdown</h3>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="rounded-xl border border-cream-warm bg-cream p-4 text-center">
                        <div className="text-xs text-[#888] uppercase tracking-wider mb-1">Origination Fees</div>
                        <div className="text-2xl font-bold text-[#0A0A0A]">{fmtUSD(revenueData.total_origination_fees)}</div>
                      </div>
                      <div className="rounded-xl border border-cream-warm bg-cream p-4 text-center">
                        <div className="text-xs text-[#888] uppercase tracking-wider mb-1">Platform Fees</div>
                        <div className="text-2xl font-bold text-[#0A0A0A]">{fmtUSD(revenueData.total_platform_fees)}</div>
                      </div>
                      <div className="rounded-xl border border-cream-warm bg-cream p-4 text-center">
                        <div className="text-xs text-[#888] uppercase tracking-wider mb-1">Avg Loan Size</div>
                        <div className="text-2xl font-bold text-[#0A0A0A]">{fmtUSD(revenueData.avg_loan_size)}</div>
                      </div>
                    </div>
                  </Card>

                  {/* Revenue by Loan Type */}
                  {revenueData.by_type.length > 0 && (
                    <Card hover={false}>
                      <h3 className="text-lg font-bold text-[#0A0A0A] mb-4">Revenue by Loan Type</h3>
                      <div className="space-y-3">
                        {revenueData.by_type.map((item, i) => (
                          <div key={item.type} className="flex items-center gap-4">
                            <span className="text-sm font-medium text-[#0A0A0A] w-24">{item.label}</span>
                            <div className="flex-1 h-8 rounded-full bg-cream border border-cream-warm overflow-hidden">
                              <motion.div initial={{ width: 0 }} animate={{ width: `${(item.revenue / Math.max(...revenueData.by_type.map(t => t.revenue), 1)) * 100}%` }}
                                transition={{ delay: i * 0.1, duration: 0.5 }}
                                className="h-full rounded-full flex items-center pl-3"
                                style={{ background: `linear-gradient(90deg, ${CHART_COLORS[i % CHART_COLORS.length]}88, ${CHART_COLORS[i % CHART_COLORS.length]})` }}>
                                <span className="text-xs font-bold text-white whitespace-nowrap">${(item.revenue / 1e3).toFixed(1)}k</span>
                              </motion.div>
                            </div>
                            <span className="text-xs text-[#888] w-16 text-right">{item.count} deals</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}

                  {/* Recent Funded Deals */}
                  {revenueData.recent_funded.length > 0 && (
                    <Card hover={false}>
                      <h3 className="text-lg font-bold text-[#0A0A0A] mb-4">Recent Funded Deals</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-cream-warm text-left">
                              <th className="pb-3 text-xs uppercase tracking-wider text-emerald-700/70 font-medium">Borrower</th>
                              <th className="pb-3 text-xs uppercase tracking-wider text-emerald-700/70 font-medium">Property</th>
                              <th className="pb-3 text-xs uppercase tracking-wider text-emerald-700/70 font-medium">Loan</th>
                              <th className="pb-3 text-xs uppercase tracking-wider text-emerald-700/70 font-medium">Revenue</th>
                              <th className="pb-3 text-xs uppercase tracking-wider text-emerald-700/70 font-medium">Funded</th>
                            </tr>
                          </thead>
                          <tbody>
                            {revenueData.recent_funded.map(f => (
                              <tr key={f.funded_id} className="border-b border-cream-warm">
                                <td className="py-3 text-[#0A0A0A] font-medium">{f.borrower_name || "Unknown Borrower"}</td>
                                <td className="py-3 text-[#555] truncate max-w-[200px]">{f.property_address || "—"}</td>
                                <td className="py-3 text-[#0A0A0A]">${(f.loan_amount / 1e3).toFixed(0)}k</td>
                                <td className="py-3 font-bold text-emerald-600">{fmtUSD(f.total_revenue)}</td>
                                <td className="py-3 text-xs text-[#999]">{new Date(f.funded_at).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  )}

                  {/* Fund a Deal Button */}
                  <Card hover={false}>
                    <h3 className="text-lg font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><Zap className="h-5 w-5 text-gold-dark" /> Record Deal Funding</h3>
                    {!fundingDeal ? (
                      <div>
                        <p className="text-sm text-[#555] mb-4">Select a deal from the pipeline to record funding and calculate revenue.</p>
                        <div className="grid gap-2 max-h-60 overflow-y-auto">
                          {(data?.recent_leads || []).filter(l => l.stage !== "funded" && l.stage !== "declined").map(lead => (
                            <button key={lead.lead_id} onClick={() => { setFundingDeal(lead); setFundForm(f => ({ ...f, loan_amount: String(lead.requested_loan_amount || lead.purchase_price || "") })); }}
                              data-testid={`fund-select-${lead.lead_id}`}
                              className="flex items-center justify-between rounded-xl border border-cream-warm bg-cream p-3 text-left hover:border-gold/40 transition-colors">
                              <div>
                                <span className="text-sm font-medium text-[#0A0A0A]">{lead.full_name || "Unknown Borrower"}</span>
                                <span className="text-xs text-[#888] ml-2">{lead.property_address}</span>
                              </div>
                              <span className="text-sm font-bold text-[#0A0A0A]">${((lead.requested_loan_amount || lead.purchase_price || 0) / 1e3).toFixed(0)}k</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4" data-testid="fund-deal-form">
                        <div className="rounded-xl border border-gold/30 bg-gold/5 p-3">
                          <span className="text-sm font-semibold text-[#0A0A0A]">{fundingDeal.full_name || "Unknown Borrower"}</span>
                          <span className="text-xs text-[#888] ml-2">{fundingDeal.property_address}</span>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <label className="block">
                            <span className="text-xs text-[#555] font-medium">Loan Amount *</span>
                            <input type="number" value={fundForm.loan_amount} onChange={e => setFundForm(f => ({ ...f, loan_amount: e.target.value }))}
                              className="w-full mt-1 rounded-xl border border-cream-warm px-3 py-2 text-sm" data-testid="fund-loan-amount" />
                          </label>
                          <label className="block">
                            <span className="text-xs text-[#555] font-medium">Lender Name</span>
                            <input value={fundForm.lender_name} onChange={e => setFundForm(f => ({ ...f, lender_name: e.target.value }))}
                              className="w-full mt-1 rounded-xl border border-cream-warm px-3 py-2 text-sm" data-testid="fund-lender-name" />
                          </label>
                          <label className="block">
                            <span className="text-xs text-[#555] font-medium">Origination Fee %</span>
                            <input type="number" step="0.1" value={fundForm.origination_fee_pct} onChange={e => setFundForm(f => ({ ...f, origination_fee_pct: e.target.value }))}
                              className="w-full mt-1 rounded-xl border border-cream-warm px-3 py-2 text-sm" />
                          </label>
                          <label className="block">
                            <span className="text-xs text-[#555] font-medium">Platform Fee %</span>
                            <input type="number" step="0.1" value={fundForm.platform_fee_pct} onChange={e => setFundForm(f => ({ ...f, platform_fee_pct: e.target.value }))}
                              className="w-full mt-1 rounded-xl border border-cream-warm px-3 py-2 text-sm" />
                          </label>
                        </div>
                        {fundForm.loan_amount && (
                          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center justify-between">
                            <span className="text-sm text-emerald-700">Projected Revenue:</span>
                            <span className="text-lg font-bold text-emerald-700">
                              {fmtUSD(parseFloat(fundForm.loan_amount) * ((parseFloat(fundForm.origination_fee_pct) + parseFloat(fundForm.platform_fee_pct)) / 100))}
                            </span>
                          </div>
                        )}
                        <div className="flex gap-3">
                          <GoldButton onClick={() => handleFundDeal(fundingDeal.lead_id)} disabled={fundLoading || !fundForm.loan_amount} data-testid="fund-submit-btn">
                            {fundLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record Funding"}
                          </GoldButton>
                          <button onClick={() => setFundingDeal(null)} className="px-4 py-2 rounded-xl border border-cream-warm text-sm text-[#555] hover:bg-cream transition-colors">Cancel</button>
                        </div>
                      </div>
                    )}
                  </Card>
                </>
              )}
            </div>
          )}

          {/* === CRM SECTION === */}
          {activeSection === "crm_dashboard" && (
            <CRMDashboard onSectionChange={setActiveSection} />
          )}

          {activeSection === "borrowers" && (
            <CRMBorrowers onViewDeal={(leadId) => setDetailLeadId(leadId)} onEntityNavigate={handleDeal360Navigate} />
          )}

          {activeSection === "lenders" && (
            <CRMLenders onViewDeal={(leadId) => setDetailLeadId(leadId)} onEntityNavigate={handleDeal360Navigate} />
          )}

          {activeSection === "investors" && (
            <CRMInvestors onViewDeal={(leadId) => setDetailLeadId(leadId)} onEntityNavigate={handleDeal360Navigate} />
          )}

          {activeSection === "email_outreach" && (
            <EmailOutreachPanel onViewDeal={(leadId) => setDetailLeadId(leadId)} onEntityNavigate={navigateToEntity} />
          )}

          {/* Document Governance Tab */}
          {activeSection === "doc_governance" && (
            <DocumentGovernancePanel onViewDeal={(leadId) => { setSelectedLead(leadId); setActiveSection("command_center"); }} />
          )}

          {/* Marketplace Preview */}
          {activeSection === "marketplace_preview" && (
            <MarketplacePreview />
          )}

          {/* AI Copilot */}
          {activeSection === "copilot" && (
            <CopilotPanel />
          )}

          {/* AI Agents */}
          {activeSection === "ai_agents" && (
            <AgentPerformancePanel />
          )}

          {/* Engagement Scoring */}
          {activeSection === "engagement_scoring" && (
            <EngagementScoringPanel />
          )}

          {/* Activity Feed */}
          {activeSection === "activity_feed" && (
            <AdminActivityFeed />
          )}

          {/* Unified Inbox */}
          {activeSection === "inbox" && (
            <UnifiedInbox onEntityNavigate={navigateToEntity} />
          )}

          {/* Security Dashboard */}
          {activeSection === "security" && (
            <SecurityDashboard />
          )}

          {/* Maker-Checker Dashboard */}
          {activeSection === "maker_checker" && (
            <MakerCheckerPanel onEntityNavigate={navigateToEntity} />
          )}

          {/* Tax Exports */}
          {activeSection === "tax_exports" && (
            <TaxExportPanel isAdmin={true} />
          )}

          {/* Investor Digests */}
          {activeSection === "investor_digests" && (
            <DigestPanel />
          )}

          {/* Exception Queue */}
          {activeSection === "exception_queue" && (
            <ExceptionQueuePanel onEntityNavigate={navigateToEntity} />
          )}

          {/* Integrity Monitoring */}
          {activeSection === "integrity" && (
            <IntegrityDashboard />
          )}

          {/* Platform Health */}
          {activeSection === "platform_health" && (
            <PlatformHealthPage />
          )}

          {/* Team Management */}
          {activeSection === "team" && (
            <TeamManagement />
          )}

          {/* PostgreSQL Migration Dashboard */}
          {activeSection === "migration" && (
            <MigrationDashboard />
          )}

          {/* Analytics */}
          {activeSection === "analytics" && (
            <div className="space-y-6">
              <AdminFunnelAnalytics />
              <AnalyticsTab data={analyticsData} loading={analyticsLoading} />
            </div>
          )}
            </div>{/* end flex-1 */}
          </div>{/* end flex */}
        </section>
      </div>

      {/* Deal Detail Modal */}
      {detailLeadId && <DealDetailModal leadId={detailLeadId} onClose={() => setDetailLeadId(null)} viewerRole="admin" />}

      {/* Deal 360 Overlay */}
      {deal360OverlayId && (
        <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto"
          data-testid="deal360-overlay">
          <div className="relative w-full max-w-7xl mx-4 my-6 bg-[#faf7f2] rounded-2xl shadow-2xl border border-[#e8e0d0] min-h-[80vh]">
            <button onClick={() => setDeal360OverlayId(null)}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 border border-[#e8e0d0] hover:bg-white hover:border-red-200 transition-colors group"
              data-testid="deal360-overlay-close">
              <svg className="h-5 w-5 text-[#999] group-hover:text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="p-6">
              <Deal360Page leadId={deal360OverlayId}
                onBack={() => setDeal360OverlayId(null)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const CHART_COLORS = ["#E5C98B", "#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial" };

const chartTooltipStyle = {
  contentStyle: { background: "#022c22", border: "1px solid rgba(229,201,139,0.3)", borderRadius: "12px", color: "#E5C98B", fontSize: "13px" },
  labelStyle: { color: "#f5f0e8" },
  itemStyle: { color: "#f5f0e8" },
};

function AnalyticsTab({ data, loading }) {
  if (loading || !data) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
    </div>
  );

  const { daily_volume = [], stage_funnel = [], loan_distribution = [], risk_distribution = {}, user_counts = {}, state_distribution = [], engagement = {}, conversion = {}, revenue = {}, lender_metrics = {} } = data;

  const loanPieData = loan_distribution.map((d, i) => ({
    name: LOAN_LABELS[d.type] || d.type || "Other", value: d.count, volume: d.volume, fill: CHART_COLORS[i % CHART_COLORS.length],
  }));

  const riskPieData = [
    { name: "Low Risk", value: risk_distribution.low || 0, fill: "#10B981" },
    { name: "Moderate", value: risk_distribution.moderate || 0, fill: "#F59E0B" },
    { name: "Elevated", value: risk_distribution.elevated || 0, fill: "#EF4444" },
  ].filter(d => d.value > 0);

  const userBarData = [
    { name: "Borrowers", count: user_counts.borrowers || 0, fill: "#10B981" },
    { name: "Lenders", count: user_counts.lenders || 0, fill: "#3B82F6" },
    { name: "Admins", count: user_counts.admins || 0, fill: "#E5C98B" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Deal Volume", value: `$${(daily_volume.reduce((s, d) => s + (d.volume || 0), 0) / 1e6).toFixed(1)}M`, icon: DollarSign, color: "text-gold-dark" },
          { label: "Deals (30d)", value: daily_volume.reduce((s, d) => s + d.count, 0), icon: TrendingUp, color: "text-emerald-600" },
          { label: "Avg Interest/Deal", value: engagement.avg_interest_per_deal || 0, icon: Activity, color: "text-blue-600" },
          { label: "Avg Offers/Deal", value: engagement.avg_term_sheets_per_deal || 0, icon: FileText, color: "text-amber-600" },
        ].map((kpi, i) => (
          <motion.div key={kpi.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <Card hover={false}>
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-gold/10 p-2.5 border border-cream-warm"><kpi.icon className={`h-5 w-5 ${kpi.color}`} /></div>
                <div>
                  <div className="text-2xl font-bold text-[#0A0A0A]">{kpi.value}</div>
                  <div className="text-xs text-[#888]">{kpi.label}</div>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Funding Velocity & Investor Demand */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card hover={false} data-testid="funding-velocity">
          <h3 className="text-sm font-bold text-[#333] mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-teal-600" /> Funding Velocity
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-teal-50 border border-teal-200 text-center">
              <div className="text-2xl font-bold text-teal-700">{data?.funding_velocity?.avg_days_to_fund || 0}</div>
              <div className="text-[10px] text-teal-600">Avg Days to Fund</div>
            </div>
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
              <div className="text-2xl font-bold text-emerald-700">{data?.funding_velocity?.fastest_funding_days || 0}</div>
              <div className="text-[10px] text-emerald-600">Fastest (days)</div>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
              <div className="text-2xl font-bold text-blue-700">{data?.funding_velocity?.funded_deals_count || 0}</div>
              <div className="text-[10px] text-blue-600">Funded Deals</div>
            </div>
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-center">
              <div className="text-2xl font-bold text-amber-700">${((data?.funding_velocity?.total_outstanding || 0) / 1e6).toFixed(2)}M</div>
              <div className="text-[10px] text-amber-600">Outstanding</div>
            </div>
          </div>
        </Card>
        <Card hover={false} data-testid="investor-demand">
          <h3 className="text-sm font-bold text-[#333] mb-3 flex items-center gap-2">
            <Users className="h-4 w-4 text-indigo-600" /> Investor Demand
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-center">
              <div className="text-2xl font-bold text-indigo-700">{data?.investor_demand?.registered_investors || 0}</div>
              <div className="text-[10px] text-indigo-600">Investors</div>
            </div>
            <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 text-center">
              <div className="text-2xl font-bold text-purple-700">${((data?.investor_demand?.total_committed || 0) / 1e3).toFixed(0)}k</div>
              <div className="text-[10px] text-purple-600">Committed</div>
            </div>
            <div className="p-3 rounded-lg bg-cyan-50 border border-cyan-200 text-center">
              <div className="text-2xl font-bold text-cyan-700">{data?.investor_demand?.commitment_count || 0}</div>
              <div className="text-[10px] text-cyan-600">Commitments</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Deal Volume Over Time */}
      <Card hover={false}>
        <h3 className="text-lg font-bold text-[#0A0A0A] mb-1 flex items-center gap-2"><BarChart3 className="h-5 w-5 text-gold-dark" /> Deal Volume Over Time</h3>
        <p className="text-xs text-[#999] mb-4">Last 30 days</p>
        {daily_volume.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={daily_volume}>
              <defs>
                <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E5C98B" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#E5C98B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(229,201,139,0.1)" />
              <XAxis dataKey="date" tick={{ fill: "#f5f0e880", fontSize: 11 }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fill: "#f5f0e880", fontSize: 11 }} tickFormatter={v => `$${(v / 1e6).toFixed(1)}M`} />
              <Tooltip {...chartTooltipStyle} formatter={(v, name) => name === "volume" ? [`$${(v / 1e6).toFixed(2)}M`, "Volume"] : [v, "Deals"]} />
              <Area type="monotone" dataKey="volume" stroke="#E5C98B" strokeWidth={2} fill="url(#volGrad)" />
              <Area type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2} fill="transparent" />
            </AreaChart>
          </ResponsiveContainer>
        ) : <p className="text-[#999] text-center py-12">No deal volume data available</p>}
      </Card>

      {/* Conversion Funnel */}
      {conversion.total_leads > 0 && (
        <Card hover={false} data-testid="conversion-funnel">
          <h3 className="text-lg font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><Target className="h-5 w-5 text-gold-dark" /> Conversion Funnel</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Total Leads", value: conversion.total_leads, rate: "100%", color: "bg-slate-200" },
              { label: "Qualified Deals", value: conversion.qualified_deals, rate: `${conversion.lead_conversion_rate}%`, color: "bg-blue-200" },
              { label: "Matched", value: conversion.matched_deals, rate: `${conversion.match_rate}%`, color: "bg-emerald-200" },
              { label: "Funded", value: conversion.funded_deals, rate: `${conversion.fund_rate}%`, color: "bg-gold/40" },
            ].map((step, i) => (
              <div key={step.label} className="relative">
                <div className="rounded-xl border border-cream-warm bg-cream p-4 text-center relative z-10">
                  <div className="text-2xl font-bold text-[#0A0A0A]">{step.value}</div>
                  <div className="text-xs text-[#888] mb-2">{step.label}</div>
                  <div className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${step.color} text-[#0A0A0A]`}>{step.rate}</div>
                </div>
                {i < 3 && <div className="absolute top-1/2 -right-2 z-20 text-cream-warm"><ArrowRight className="h-4 w-4" /></div>}
              </div>
            ))}
          </div>
          {revenue.total_revenue > 0 && (
            <div className="mt-4 grid sm:grid-cols-3 gap-3 pt-4 border-t border-cream-warm">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center">
                <div className="text-xs text-emerald-600 uppercase tracking-wider">Total Revenue</div>
                <div className="text-xl font-bold text-emerald-700">${(revenue.total_revenue / 1e3).toFixed(1)}k</div>
              </div>
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-center">
                <div className="text-xs text-blue-600 uppercase tracking-wider">Funded Volume</div>
                <div className="text-xl font-bold text-blue-700">${(revenue.total_funded_volume / 1e6).toFixed(2)}M</div>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-center">
                <div className="text-xs text-amber-600 uppercase tracking-wider">Active Lenders</div>
                <div className="text-xl font-bold text-amber-700">{lender_metrics.active_lenders || 0}</div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Stage Funnel + Loan Distribution */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card hover={false}>
          <h3 className="text-lg font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-gold-dark" /> Pipeline Funnel</h3>
          {stage_funnel.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stage_funnel} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(229,201,139,0.1)" />
                <XAxis type="number" tick={{ fill: "#f5f0e880", fontSize: 11 }} />
                <YAxis dataKey="label" type="category" tick={{ fill: "#f5f0e880", fontSize: 11 }} width={90} />
                <Tooltip {...chartTooltipStyle} />
                <Bar dataKey="count" fill="#E5C98B" radius={[0, 6, 6, 0]} barSize={20}>
                  {stage_funnel.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-[#999] text-center py-12">No funnel data</p>}
        </Card>

        <Card hover={false}>
          <h3 className="text-lg font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><PieChart className="h-5 w-5 text-gold-dark" /> Loan Type Distribution</h3>
          {loanPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <RechartsPie>
                <Pie data={loanPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={4} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {loanPieData.map((d, i) => <Cell key={i} fill={d.fill} stroke="transparent" />)}
                </Pie>
                <Tooltip {...chartTooltipStyle} formatter={(v, name, props) => [`${v} deals ($${((props.payload.volume || 0) / 1e6).toFixed(1)}M)`, name]} />
              </RechartsPie>
            </ResponsiveContainer>
          ) : <p className="text-[#999] text-center py-12">No loan data</p>}
        </Card>
      </div>

      {/* Risk + Users + Geography */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card hover={false}>
          <h3 className="text-base font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><Shield className="h-4 w-4 text-gold-dark" /> Risk Profile</h3>
          {riskPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <RechartsPie>
                <Pie data={riskPieData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={4} dataKey="value">
                  {riskPieData.map((d, i) => <Cell key={i} fill={d.fill} stroke="transparent" />)}
                </Pie>
                <Tooltip {...chartTooltipStyle} />
                <Legend wrapperStyle={{ fontSize: "12px", color: "#f5f0e8" }} />
              </RechartsPie>
            </ResponsiveContainer>
          ) : <p className="text-[#999] text-center py-8 text-sm">No risk data</p>}
        </Card>

        <Card hover={false}>
          <h3 className="text-base font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><Users className="h-4 w-4 text-gold-dark" /> User Breakdown</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={userBarData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(229,201,139,0.1)" />
              <XAxis dataKey="name" tick={{ fill: "#f5f0e880", fontSize: 11 }} />
              <YAxis tick={{ fill: "#f5f0e880", fontSize: 11 }} />
              <Tooltip {...chartTooltipStyle} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={30}>
                {userBarData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card hover={false}>
          <h3 className="text-base font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><MapPin className="h-4 w-4 text-gold-dark" /> Top States</h3>
          <div className="space-y-2.5">
            {state_distribution.slice(0, 8).map((s, i) => {
              const maxCount = state_distribution[0]?.count || 1;
              return (
                <div key={s.state || i} className="flex items-center gap-3">
                  <span className="text-sm text-[#444] w-8 font-medium">{s.state || "N/A"}</span>
                  <div className="flex-1 h-5 rounded-full bg-cream border border-cream-warm overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${(s.count / maxCount) * 100}%` }} transition={{ delay: i * 0.08, duration: 0.5 }}
                      className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${CHART_COLORS[i % CHART_COLORS.length]}88, ${CHART_COLORS[i % CHART_COLORS.length]})` }} />
                  </div>
                  <span className="text-xs text-[#888] w-6 text-right">{s.count}</span>
                </div>
              );
            })}
            {state_distribution.length === 0 && <p className="text-[#999] text-center py-8 text-sm">No geographic data</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}


const ALL_ROLES = [
  { value: "admin", label: "Admin", color: "bg-red-100 text-red-600" },
  { value: "borrower", label: "Borrower", color: "bg-emerald-100 text-emerald-600" },
  { value: "lender", label: "Lender", color: "bg-blue-100 text-blue-600" },
  { value: "investor", label: "Investor", color: "bg-amber-100 text-amber-600" },
  { value: "originations", label: "Originations", color: "bg-teal-100 text-teal-600" },
  { value: "underwriter", label: "Underwriter", color: "bg-indigo-100 text-indigo-600" },
  { value: "processor", label: "Processor", color: "bg-cyan-100 text-cyan-600" },
  { value: "lender_relations", label: "Lender Relations", color: "bg-lime-100 text-lime-600" },
  { value: "investor_relations", label: "Investor Relations", color: "bg-orange-100 text-orange-600" },
];

function getRoleStyle(role) {
  return ALL_ROLES.find(r => r.value === role)?.color || "bg-slate-100 text-slate-600";
}

function UserManagementSection({ users, loading: usersLoading, isFullView, onRoleChanged }) {
  const [updatingRole, setUpdatingRole] = useState(null);

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingRole(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole });
      onRoleChanged();
    } catch (err) {
      console.error(err);
    } finally {
      setUpdatingRole(null);
    }
  };

  return (
    <Card hover={false} className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-[#0A0A0A]" data-testid="users-section-title">
          {isFullView ? "User Management" : "Recent Users"}
        </h3>
        {isFullView && (
          <span className="text-xs text-[#888]">{users.length} users</span>
        )}
      </div>
      {usersLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-cream-warm text-left">
                <th className="pb-3 text-xs uppercase tracking-wider text-emerald-700/70 font-medium">Name</th>
                <th className="pb-3 text-xs uppercase tracking-wider text-emerald-700/70 font-medium">Email</th>
                <th className="pb-3 text-xs uppercase tracking-wider text-emerald-700/70 font-medium">Role</th>
                <th className="pb-3 text-xs uppercase tracking-wider text-emerald-700/70 font-medium">Status</th>
                <th className="pb-3 text-xs uppercase tracking-wider text-emerald-700/70 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.user_id || i} className="border-b border-cream-warm hover:bg-cream/50 transition-colors" data-testid={`user-row-${u.user_id}`}>
                  <td className="py-3 text-[#0A0A0A] font-medium">{u.full_name}</td>
                  <td className="py-3 text-[#555]">{u.email}</td>
                  <td className="py-3">
                    {isFullView ? (
                      <div className="relative">
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u.user_id, e.target.value)}
                          disabled={updatingRole === u.user_id}
                          data-testid={`role-select-${u.user_id}`}
                          className={`px-2 py-1 rounded-lg text-xs font-medium border appearance-none cursor-pointer pr-6 ${getRoleStyle(u.role)} disabled:opacity-50`}
                        >
                          {ALL_ROLES.map(r => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                        {updatingRole === u.user_id && (
                          <Loader2 className="absolute right-1 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-emerald-600" />
                        )}
                      </div>
                    ) : (
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getRoleStyle(u.role)}`}>{u.role}</span>
                    )}
                  </td>
                  <td className="py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                      u.status === "active" ? "bg-emerald-100 text-emerald-600" :
                      u.status === "pending" ? "bg-amber-100 text-amber-600" :
                      "bg-slate-100 text-slate-500"
                    }`}>{u.status || "active"}</span>
                  </td>
                  <td className="py-3 text-[#999] text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


function AdminActivityFeed() {
  const { navigateToEntity } = usePageNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const token = localStorage.getItem("ic_token");
  useEffect(() => {
    axios.get(`${API}/activity/feed?limit=50`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setEvents(r.data.events || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? events : events.filter(e => e.type === filter);
  const typeColors = { timeline: "bg-slate-100 text-slate-600", notification: "bg-amber-50 text-amber-600", commitment: "bg-emerald-50 text-emerald-600" };
  const typeIcons = { timeline: Clock, notification: Bell, commitment: DollarSign };

  return (
    <div data-testid="admin-activity-feed">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-[#222]">Platform Activity Feed</h2>
        <div className="flex gap-1.5">
          {["all", "timeline", "notification", "commitment"].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`activity-filter-${f}`}
              className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-all capitalize ${filter === f ? "bg-emerald-700 text-white" : "bg-white border border-cream-warm text-[#666]"}`}>
              {f}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>
      ) : filtered.length === 0 ? (
        <Card hover={false}><div className="text-center py-8 text-[#999] text-sm">No activity events</div></Card>
      ) : (
        <div className="space-y-0">
          {filtered.slice(0, 30).map((event, i) => {
            const Icon = typeIcons[event.type] || Clock;
            const colorClass = typeColors[event.type] || "bg-gray-100 text-gray-600";
            return (
              <div key={`${event.timestamp}-${i}`} className="flex gap-3 group" data-testid="admin-activity-event">
                <div className="flex flex-col items-center">
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${colorClass} ring-2 ring-white`}>
                    <Icon className="h-3 w-3" />
                  </div>
                  {i < filtered.length - 1 && <div className="w-px flex-1 bg-[#e8e0d0] min-h-[8px]" />}
                </div>
                <div className="flex-1 pb-3">
                  <p className="text-xs text-[#222]">{event.description}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-[#bbb]">{event.timestamp ? new Date(event.timestamp).toLocaleString() : ""}</span>
                    {event.subtype && <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{event.subtype.replace(/_/g, " ")}</span>}
                  </div>
                </div>
                {event.entity_id && (
                  <button onClick={() => navigateToEntity(event.entity_type, event.entity_id)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-emerald-50 text-[#999] transition-all shrink-0" data-testid="admin-activity-navigate">
                    <ChevronRight className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
